import { c as _c } from "react/compiler-runtime";
import { useConfirmation } from '@/hooks/useConfirmation';
import useTranslation from '@/hooks/useTranslation';
import classNames from '@/utils/classNames';
import { Button, Input } from '@bloomberg/bbui.react';
import CheckmarkIcon from '@bloomberg/web-icons-react/Checkmark';
import DownloadIcon from '@bloomberg/web-icons-react/Download';
import DuplicateIcon from '@bloomberg/web-icons-react/Duplicate';
import QRCode from 'qrcode';
import { useEffect, useRef } from 'react';
import type { FCl } from 'react-fc';
import { clipboardCopy } from 'web/src/utils/siteUtils';
import styles from './QrCode.module.css';
type ConfirmedButton = 'copyImage' | 'copyValue' | 'download';
export const QrCode: FCl<{
  value: string;
}> = t0 => {
  const $ = _c(50);
  const {
    value,
    className
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    confirmed,
    showConfirmation
  } = useConfirmation();
  const ref = useRef(null);
  let t1;
  let t2;
  if ($[0] !== value) {
    t1 = () => {
      if (value && ref.current) {
        let qrValue = value;
        try {
          const url = new URL(value);
          url.searchParams.set("e", "qr");
          qrValue = url.toString();
        } catch {}
        QRCode.toCanvas(ref.current, qrValue, {
          width: 336,
          margin: 2
        });
      }
    };
    t2 = [value];
    $[0] = value;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== showConfirmation) {
    t3 = async () => {
      if (!ref.current) {
        return;
      }
      try {
        const blob = await new Promise(resolve => ref.current.toBlob(resolve, "image/png"));
        if (blob) {
          await navigator.clipboard.write([new ClipboardItem({
            "image/png": blob
          })]);
          showConfirmation("copyImage");
        }
      } catch {}
    };
    $[3] = showConfirmation;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleCopyImage = t3;
  let t4;
  if ($[5] !== showConfirmation || $[6] !== value) {
    t4 = async () => {
      try {
        await clipboardCopy(value);
        showConfirmation("copyValue");
      } catch {}
    };
    $[5] = showConfirmation;
    $[6] = value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleCopyValue = t4;
  let t5;
  if ($[8] !== showConfirmation) {
    t5 = () => {
      if (!ref.current) {
        return;
      }
      const link = document.createElement("a");
      link.download = "qrcode.png";
      link.href = ref.current.toDataURL("image/png");
      link.click();
      showConfirmation("download");
    };
    $[8] = showConfirmation;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleDownload = t5;
  let t6;
  if ($[10] !== className) {
    t6 = classNames(styles.grid, className);
    $[10] = className;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== handleCopyValue) {
    t7 = e => {
      (e.target as HTMLInputElement).select();
      handleCopyValue();
    };
    $[12] = handleCopyValue;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7 || $[15] !== value) {
    t8 = <Input readOnly={true} value={value} onClick={t7} />;
    $[14] = t7;
    $[15] = value;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== confirmed) {
    t9 = confirmed === "copyValue" ? <CheckmarkIcon /> : <DuplicateIcon />;
    $[17] = confirmed;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== confirmed || $[20] !== t) {
    t10 = confirmed === "copyValue" ? t("common:Modals.ShareHandler.linkCopied") as string : t("common:Modals.ShareHandler.copyLink") as string;
    $[19] = confirmed;
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== handleCopyValue || $[23] !== t10 || $[24] !== t9) {
    t11 = <Button kind="tertiary" icon={t9} onClick={handleCopyValue}>{t10}</Button>;
    $[22] = handleCopyValue;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <canvas className={styles.canvas} ref={ref} />;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== confirmed) {
    t13 = confirmed === "copyImage" ? <CheckmarkIcon /> : <DuplicateIcon />;
    $[27] = confirmed;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== confirmed || $[30] !== t) {
    t14 = confirmed === "copyImage" ? t("common:Modals.ShareHandler.copied") as string : t("common:Modals.ShareHandler.copyQrCode") as string;
    $[29] = confirmed;
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== handleCopyImage || $[33] !== t13 || $[34] !== t14) {
    t15 = <Button kind="tertiary" icon={t13} onClick={handleCopyImage}>{t14}</Button>;
    $[32] = handleCopyImage;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== confirmed) {
    t16 = confirmed === "download" ? <CheckmarkIcon /> : <DownloadIcon />;
    $[36] = confirmed;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t("common:Modals.ShareHandler.downloadQrCode");
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const t18 = t17 as string;
  let t19;
  if ($[40] !== handleDownload || $[41] !== t16 || $[42] !== t18) {
    t19 = <Button kind="tertiary" icon={t16} onClick={handleDownload}>{t18}</Button>;
    $[40] = handleDownload;
    $[41] = t16;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t11 || $[45] !== t15 || $[46] !== t19 || $[47] !== t6 || $[48] !== t8) {
    t20 = <div className={t6}>{t8}{t11}{t12}{t15}{t19}</div>;
    $[44] = t11;
    $[45] = t15;
    $[46] = t19;
    $[47] = t6;
    $[48] = t8;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  return t20;
};